import { useState, type FormEvent } from "react";
import useSeo, { SITE_URL } from "@/hooks/useSeo";
import Breadcrumb from "@/components/feature/Breadcrumb";

const ABOUT_IMG =
  "https://readdy.ai/api/search-image?query=Warm%20stylized%20manga%20illustration%20of%20a%20friendly%20creative%20team%20of%20editors%20working%20together%20around%20a%20table%20with%20comic%20panels%20and%20storyboards%2C%20cozy%20studio%20atmosphere%2C%20soft%20cream%20and%20coral%20palette%2C%20collaborative%20cheerful%20editorial%20anime%20art&width=1200&height=800&seq=dm-about-team&orientation=landscape";

const testimonials = [
  {
    name: "小陈 · 程序员",
    content: "每天通勤半小时全靠抖漫速看撑过来，一部好漫画几分钟就能了解全貌，入坑效率太高了。",
  },
  {
    name: "林同学 · 大学生",
    content: "睡前放松分类真的绝，画风温柔的漫画读完刚好入睡，已经成了我的睡前仪式。",
  },
  {
    name: "阿岚 · 编辑",
    content: "抖漫片段的选取眼光很准，把最精彩的名场面剪出来，碎片时间也能过足瘾。",
  },
];

const values = [
  { icon: "ri-timer-line", title: "高效", text: "把内容切得恰好能填满碎片时间，通勤睡前都能看完一段。", color: "text-primary-600 bg-primary-100" },
  { icon: "ri-sparkling-2-line", title: "精选", text: "每一部作品都经编辑严选，宁缺毋滥，把时间留给好内容。", color: "text-accent-700 bg-accent-100" },
  { icon: "ri-heart-3-line", title: "治愈", text: "专注温暖、治愈、有共鸣的内容，让休息时间真的被治愈。", color: "text-secondary-800 bg-secondary-100" },
];

const FORM_URL = "https://readdy.ai/api/form/da08o2o3256a3o8kor8g";

export default function AboutPage() {
  useSeo({
    title: "关于抖漫 - 品牌介绍与联系我们",
    description:
      "了解抖漫的品牌故事与愿景，我们专注碎片化漫画观看场景，提供抖漫速看、抖漫精选、抖漫片段内容。欢迎通过表单或联系方式与我们沟通。",
    keywords: "关于抖漫,抖漫品牌,抖漫联系方式,漫画平台",
    path: "/about",
    type: "website",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于抖漫",
        url: `${SITE_URL}/about`,
        inLanguage: "zh-CN",
        isPartOf: { "@type": "WebSite", name: "抖漫", url: SITE_URL },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: `${SITE_URL}/` },
          { "@type": "ListItem", position: 2, name: "关于我们", item: `${SITE_URL}/about` },
        ],
      },
    ],
  });

  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const honeypot = (data.get("company_alt") as string) || "";
    if (honeypot.trim() !== "") {
      setStatus("success");
      setMessage("已收到您的留言，我们会尽快与您联系！");
      form.reset();
      return;
    }
    if (honeypot.trim() === "") {
      data.delete("company_alt");
    }
    setStatus("submitting");
    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(data as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(text);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || "";
      if (res.ok && parsed?.code === "OK") {
        setStatus("success");
        setMessage(serverMsg || "已收到您的留言，我们会尽快与您联系！");
        form.reset();
      } else {
        setStatus("error");
        setMessage(serverMsg || "提交失败，请稍后再试。");
      }
    } catch {
      setStatus("error");
      setMessage("网络异常，请稍后再试。");
    }
  }

  return (
    <div className="mx-auto max-w-7xl px-4 md:px-6 py-8 md:py-12">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />

      <header className="mt-6 max-w-3xl">
        <span className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
          <i className="ri-quill-pen-line"></i> 关于抖漫
        </span>
        <h1 className="mt-3 font-heading text-3xl md:text-4xl text-foreground-950">为碎片时间而生的漫画阅读平台</h1>
      </header>

      {/* Brand intro */}
      <section className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
        <div>
          <h2 className="font-heading text-xl md:text-2xl text-foreground-950">品牌介绍</h2>
          <p className="mt-4 text-sm md:text-base leading-8 text-foreground-700">
            抖漫成立于 2024 年，我们相信在这个快节奏的时代，每个人都有享受好故事的权利。无论是早高峰通勤的地铁上，还是夜深人静临睡前的被窝里，抖漫都希望用一段恰到好处的漫画内容，填满你生活的碎片时光。
          </p>
          <p className="mt-4 text-sm md:text-base leading-8 text-foreground-700">
            我们专注打磨三类核心内容——<strong className="text-primary-600">抖漫速看</strong>、<strong className="text-primary-600">抖漫精选</strong>与<strong className="text-primary-600">抖漫片段</strong>，让用户无论在通勤、睡前还是日常休息，都能快速进入一个精彩的漫画世界。
          </p>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {values.map((v) => (
              <div key={v.title} className="rounded-lg border border-background-200 bg-background-50 p-4">
                <span className={`flex h-10 w-10 items-center justify-center rounded-full ${v.color}`}>
                  <i className={`${v.icon} text-lg`}></i>
                </span>
                <p className="mt-3 text-sm font-bold text-foreground-950">{v.title}</p>
                <p className="mt-1 text-xs leading-5 text-foreground-500">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="relative aspect-[3/2] w-full overflow-hidden rounded-lg">
          <img src={ABOUT_IMG} alt="抖漫编辑部团队" title="抖漫品牌团队" className="h-full w-full object-cover" />
        </div>
      </section>

      {/* Reputation */}
      <section className="mt-14 bg-background-100/70 py-12 md:py-16 rounded-lg" aria-label="用户口碑">
        <div className="mx-auto max-w-6xl px-4 md:px-6">
          <h2 className="text-center font-heading text-2xl md:text-3xl text-foreground-950">用户口碑</h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {testimonials.map((t) => (
              <figure key={t.name} className="rounded-lg border border-background-200 bg-background-50 p-6">
                <div className="flex gap-1 text-accent-500">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <i key={i} className="ri-star-fill"></i>
                  ))}
                </div>
                <blockquote className="mt-4 text-sm leading-7 text-foreground-700">“{t.content}”</blockquote>
                <figcaption className="mt-4 text-sm font-medium text-foreground-950">— {t.name}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* Contact info + form */}
      <section className="mt-14" aria-label="联系方式">
        <h2 className="font-heading text-2xl md:text-3xl text-foreground-950">联系我们</h2>
        <p className="mt-3 max-w-2xl text-sm md:text-base leading-7 text-foreground-600">
          无论是内容合作、版权合作还是产品建议，欢迎通过下方表单或联系方式与我们联系，我们会尽快回复。
        </p>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-4">
            {[
              { icon: "ri-mail-line", label: "邮箱", value: "hello@douman.example.com", href: "mailto:hello@douman.example.com" },
              { icon: "ri-phone-line", label: "电话", value: "400-800-2026", href: "tel:4008002026" },
              { icon: "ri-map-pin-line", label: "地址", value: "中国浙江省杭州市西湖区创意产业园", href: undefined },
              { icon: "ri-time-line", label: "工作时间", value: "周一至周五 9:00 - 18:00", href: undefined },
            ].map((c) => (
              <div key={c.label} className="flex items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-4">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-600">
                  <i className={`${c.icon} text-xl`}></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-500">{c.label}</p>
                  {c.href ? (
                    <a href={c.href} className="text-sm font-semibold text-foreground-950 hover:text-primary-600 transition-colors">
                      {c.value}
                    </a>
                  ) : (
                    <p className="text-sm font-semibold text-foreground-950">{c.value}</p>
                  )}
                </div>
              </div>
            ))}
          </div>

          <form
            id="douman-contact-form"
            data-readdy-form="contact"
            onSubmit={handleSubmit}
            className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8"
          >
            <h3 className="font-heading text-lg md:text-xl text-foreground-950">给我们留言</h3>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-foreground-800">您的称呼</label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  maxLength={30}
                  placeholder="怎么称呼您"
                  className="mt-1.5 w-full rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-foreground-800">邮箱</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  placeholder="name@example.com"
                  className="mt-1.5 w-full rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                />
              </div>
            </div>
            <div className="mt-4">
              <label htmlFor="subject" className="block text-sm font-medium text-foreground-800">主题</label>
              <select
                id="subject"
                name="subject"
                className="mt-1.5 w-full rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              >
                <option value="内容合作">内容合作</option>
                <option value="版权合作">版权合作</option>
                <option value="产品建议">产品建议</option>
                <option value="其他">其他</option>
              </select>
            </div>
            <div className="mt-4">
              <label htmlFor="message" className="block text-sm font-medium text-foreground-800">留言内容</label>
              <textarea
                id="message"
                name="message"
                required
                maxLength={500}
                rows={4}
                placeholder="想对我们说些什么（不超过500字）"
                className="mt-1.5 w-full resize-none rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              ></textarea>
            </div>

            {/* anti-spam honeypot */}
            <div className="field-company-alt">
              <label htmlFor="company_alt">公司名称</label>
              <input
                id="company_alt"
                name="company_alt"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />
            </div>

            <button
              type="submit"
              disabled={status === "submitting"}
              className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 whitespace-nowrap hover:bg-primary-600 transition-colors disabled:opacity-60"
            >
              {status === "submitting" ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i> 发送中…
                </>
              ) : (
                <>
                  <i className="ri-send-plane-line"></i> 发送留言
                </>
              )}
            </button>

            {status === "success" && (
              <p role="status" className="mt-4 flex items-center gap-2 rounded-md bg-primary-50 px-4 py-3 text-sm text-primary-800">
                <i className="ri-checkbox-circle-line"></i> {message}
              </p>
            )}
            {status === "error" && (
              <p role="alert" className="mt-4 flex items-center gap-2 rounded-md bg-primary-50 px-4 py-3 text-sm text-primary-800">
                <i className="ri-error-warning-line"></i> {message}
              </p>
            )}
          </form>
        </div>
      </section>
    </div>
  );
}